/**
 * .card, plus the .pay-card and .v3-panel variants.
 *
 * `title` renders the .card h2, which the stylesheet uppercases and (in v2)
 * decorates with corner brackets. Pass it as plain sentence case — the theme
 * decides how it is shown.
 *
 * `variant="v3"` swaps the card for a HUD panel and the h2 for a .v3-hud-label,
 * so a view that is identical apart from its chrome (the admin payout forms,
 * which V3 shows inside its Ledger) can be shared rather than rewritten.
 */
const VARIANT_CLASS = { pay: 'pay-card', v3: 'v3-panel', v3ph: 'v3-panel', v3sec: 'v3-panel' };

/**
 * `action` puts a control or a status chip on the title's right — a Show/Hide
 * toggle, a "Not connected" badge. It is a slot rather than something callers
 * position themselves, so every card header in the app lines its action up the
 * same way instead of each one inventing a flex row.
 *
 * The wrapper is only rendered WHEN an action is passed. Every existing card
 * emits exactly the markup it did before, so the stylesheet rules written
 * against a bare `.card > h2` keep matching.
 */
export default function Card({ title, variant, action, className = '', children, ...rest }) {
  const classes = [VARIANT_CLASS[variant] ?? 'card', className].filter(Boolean).join(' ');
  const heading =
    title &&
    // Three V3 header styles, all from the original: .v3-ph carries the
    // accent bar the Command view uses; .v3-sec-label is the same mono label
    // without it, which the Archive's panels used; and .v3-hud-label, used
    // by the Ledger and Intel views, carries no style at all — it renders as
    // body text, so a v3 title has to arrive already in the case it should
    // be shown in.
    (variant === 'v3ph' ? (
      <div className='v3-ph'>{title}</div>
    ) : variant === 'v3sec' ? (
      <div className='v3-sec-label'>{title}</div>
    ) : variant === 'v3' ? (
      <div className='v3-hud-label'>{title}</div>
    ) : (
      <h2>{title}</h2>
    ));

  return (
    <div className={classes} {...rest}>
      {action ? (
        <div className='card__head'>
          {heading}
          <div className='card__action'>{action}</div>
        </div>
      ) : (
        heading
      )}
      {children}
    </div>
  );
}
